import type { S2SEventEnvelope } from "../socket_handler/data_structures/to_server/custom_event_request.js";

const STORAGE_KEY = 's2s_event_outbox_v1';
const MAX_PENDING_EVENTS = 100;
const MAX_DELIVERY_ATTEMPTS = 20;
const MAX_EVENT_AGE_MS = 7 * 24 * 60 * 60_000;

export type S2SOutboxEntry = {
  event: S2SEventEnvelope;
  attempts: number;
  next_attempt_at: number;
  created_at: number;
};

function isRecord( value: unknown ): value is Record<string, unknown>
{
  return !!value && typeof value === 'object' && !Array.isArray( value );
}

function isEvent( value: unknown ): value is S2SEventEnvelope
{
  if ( !isRecord( value ) || !isRecord( value.payload ) ) return false;
  return typeof value.event_id === 'string'
    && value.event_id.length > 0
    && typeof value.event_time === 'string'
    && typeof value.first_open_time === 'string'
    && typeof value.payload.name === 'string';
}

function normalizeEntry( value: unknown ): S2SOutboxEntry | null
{
  if ( !isRecord( value ) || !isEvent( value.event ) ) return null;
  if (
    typeof value.attempts !== 'number'
    || !Number.isFinite( value.attempts )
    || value.attempts < 0
    || typeof value.next_attempt_at !== 'number'
    || !Number.isFinite( value.next_attempt_at )
    || value.next_attempt_at < 0
  )
    return null;

  const occurrence_time = Date.parse( value.event.event_time );
  const created_at = typeof value.created_at === 'number'
    && Number.isFinite( value.created_at )
    && value.created_at > 0
    ? value.created_at
    : Number.isFinite( occurrence_time ) ? occurrence_time : Date.now();

  return {
    event: value.event,
    attempts: Math.floor( value.attempts ),
    next_attempt_at: value.next_attempt_at,
    created_at,
  };
}

function loadEntries(): S2SOutboxEntry[]
{
  try
  {
    const raw = localStorage.getItem( STORAGE_KEY );
    if ( !raw ) return [];
    const parsed: unknown = JSON.parse( raw );
    if ( !Array.isArray( parsed ) ) return [];

    const now = Date.now();
    const entries = parsed
      .map( normalizeEntry )
      .filter( ( entry ): entry is S2SOutboxEntry => entry !== null );
    const active: S2SOutboxEntry[] = [];

    for ( const entry of entries )
    {
      const exhausted = entry.attempts >= MAX_DELIVERY_ATTEMPTS;
      const expired = now - entry.created_at >= MAX_EVENT_AGE_MS;
      if ( exhausted || expired )
      {
        const reason = exhausted
          ? `${entry.attempts} delivery attempts`
          : 'the seven-day retention limit';
        console.warn( `[S2S] dropping terminal ${entry.event.payload.name} event ${entry.event.event_id} after ${reason}` );
        continue;
      }
      active.push( entry );
    }

    const needs_rewrite = active.length !== parsed.length
      || parsed.some( item => !isRecord( item ) || typeof item.created_at !== 'number' );
    if ( needs_rewrite ) storeEntries( active );
    return active;
  }
  catch
  {
    return [];
  }
}

function storeEntries( entries: S2SOutboxEntry[] ): boolean
{
  try
  {
    localStorage.setItem( STORAGE_KEY, JSON.stringify( entries ) );
    return true;
  }
  catch ( error )
  {
    console.warn( '[S2S] unable to persist the event outbox', error );
    return false;
  }
}

export class S2SEventOutbox
{
  public static enqueue( event: S2SEventEnvelope ): boolean
  {
    const entries = loadEntries();
    if ( entries.some( entry => entry.event.event_id === event.event_id ) ) return true;
    if ( entries.length >= MAX_PENDING_EVENTS )
    {
      console.error( `[S2S] event outbox is full (${MAX_PENDING_EVENTS}); event was not queued` );
      return false;
    }

    entries.push( { event, attempts: 0, next_attempt_at: 0, created_at: Date.now() } );
    return storeEntries( entries );
  }

  public static peekReady( now = Date.now() ): S2SOutboxEntry | null
  {
    return loadEntries().find( entry => entry.next_attempt_at <= now ) ?? null;
  }

  public static millisecondsUntilNext( now = Date.now() ): number | null
  {
    const entries = loadEntries();
    if ( entries.length === 0 ) return null;
    return Math.max( 0, Math.min( ...entries.map( entry => entry.next_attempt_at ) ) - now );
  }

  public static recordAttempt( event_id: string, next_attempt_at: number ): void
  {
    const entries = loadEntries();
    const entry = entries.find( candidate => candidate.event.event_id === event_id );
    if ( !entry ) return;
    entry.attempts += 1;
    entry.next_attempt_at = next_attempt_at;
    storeEntries( entries );
  }

  public static acknowledge( event_id: string ): boolean
  {
    const entries = loadEntries();
    const remaining = entries.filter( entry => entry.event.event_id !== event_id );
    if ( remaining.length === entries.length ) return false;
    return storeEntries( remaining );
  }
}
